import { CITY_SOURCES as SOURCES } from '../history/sources';
import type { Discovery } from './types';

export const DISCOVERIES: readonly Discovery[] = [
  {
    id: 'landmark-waag-dam',
    theme: 'trade',
    name: 'The weigh house on the Dam',
    clue: 'Goods are weighed before they are traded. Find the weigh house that stands at an angle in front of the town hall.',
    text: 'This is the first weigh house on the Dam, in use from 1341. Goods are weighed here before they are traded. Nobody knows what the building looked like, so its size and height are a careful guess.',
  },
  {
    id: 'landmark-vismarkt',
    theme: 'trade',
    name: 'The fish market',
    clue: 'Fish is sold at two long rows of covered benches. Find them on the Dam, at the head of the Damrak.',
    text: 'Two rows of covered benches with an aisle between them. Sea fish is sold on the side of the IJ and river fish on the side of the Rokin. The benches follow the painting of 1538.',
    source: SOURCES.fish.url,
  },
  {
    id: 'landmark-dam',
    theme: 'trade',
    name: 'The Dam and its great sluice',
    clue: 'A dam of stone crosses the river Amstel. Stand on it and look for the one large arch where the water passes.',
    text: 'You are on a dam of stone across the Amstel. In the thirteenth century a dam joined the settlements on the two banks. Boats pass through a sluice: look for its large arch on the side of the Damrak.',
    source: SOURCES.sluice.url,
  },
  {
    id: 'landmark-scheepskraan',
    theme: 'trade',
    name: 'The harbour crane',
    clue: 'A very tall mast stands on a raft in the IJ, close to the row of piles. Row out to the harbour crane.',
    text: 'A harbour crane on a raft in the IJ. It was first mentioned around 1498. Its mast was 100 feet high: about 28 metres. Where exactly the raft lay and how large it was are estimates.',
  },
  {
    id: 'landmark-lijnbaan-boom',
    theme: 'trade',
    name: 'The rope walks',
    clue: 'Four very long sheds lie side by side outside the old town, on the Lastage. Find the place where rope is made.',
    text: 'A rope walk is a long shed for making rope. The painting shows four of them side by side, each 300 metres long. This one belongs to Cornelis Pietersz Boom. Walk its whole length and count your steps.',
  },
  {
    id: 'landmark-lakenramen',
    theme: 'trade',
    name: 'The cloth frames',
    clue: 'By the Amstel lies a fenced field full of long rows of dark frames. Find the place where woollen cloth is dried.',
    text: 'On these frames the woollen cloth of the city is dried and stretched. The painter drew about 13 rows in a fenced field. The frames are called ramen, and they later gave the Raamgracht its name.',
  },
  {
    id: 'landmark-lucienerf-carpenter',
    theme: 'trade',
    name: 'The carpenter’s yard',
    clue: 'A house carpenter bought the tip of a small island. Look for a house with a yard full of timber.',
    text: 'House carpenter Jacob Jansz bought this plot on 16 December 1536. A map of 1544 shows a house and a timber yard here. Whether they already stood here in 1538 is not certain: the sale does not say so.',
  },
  {
    id: 'landmark-scafferij-baerdsehuys',
    theme: 'trade',
    name: 'The city’s carpentry yard',
    clue: 'The city has a carpentry yard of its own, on an island by the Grimburgwal. Row to the Scafferij.',
    text: 'This is the Scafferij, the carpentry yard of the city itself. The map of 1544 writes its name here. You see a high building with a lower one behind it. What else stood in the yard is not known.',
  },
  {
    id: 'landmark-oude-stadhuis',
    theme: 'city',
    name: 'The town hall',
    clue: 'The city is governed from a narrow gabled building with a tower. Find it on the Dam.',
    text: 'This is the old Stadhuis, the town hall. Look for its tower and for the open court of justice at the front, the vierschaar. It follows the painting of 1538. Its true size is not known.',
    source: SOURCES.sluice.url,
  },
  {
    id: 'landmark-sint-olofspoort',
    theme: 'city',
    name: 'The gate that became a prison',
    clue: 'An old city gate is no longer used as a gate. People are locked up in it now. Find it beside the Sint-Olofskapel.',
    text: 'The Sint Olofspoort was a city gate of about 1370. In 1538 it is a prison. Look for the arched passage below and the prison windows above it.',
  },
  {
    id: 'landmark-kolk-gate',
    theme: 'city',
    name: 'The water gate under the Zeedijk',
    clue: 'A stone house stands over the water where the Zeedijk crosses it. Walk through it, or row under it.',
    text: 'This stone gate carries the Zeedijk over the Oudezijds Kolk. The water runs on through a round arch. The painting and the map of 1544 both draw it, with two rows of windows over the arch.',
  },
  {
    id: 'landmark-nieuwe-brug-west-tower',
    theme: 'city',
    name: 'The tower at the harbour mouth',
    clue: 'A round stone tower stands with its foot in the water, at the western end of the Nieuwe Brug.',
    text: 'A round tower of stone to defend the harbour mouth. The painting shows its foot in the water, two rows of small windows and a pointed roof. Beside it, stairs go down to the water.',
  },
  {
    id: 'landmark-nieuwe-brug-blockhouse',
    theme: 'city',
    name: 'The blockhouse on the Nieuwe Brug',
    clue: 'A tall, narrow house of boards stands on the long bridge across the harbour mouth.',
    text: 'A tall, narrow blockhouse of boards with a gabled roof. The painting and the map of 1544 show it on its posts. It stood on the Nieuwe Brug until 1560.',
  },
  {
    id: 'landmark-blokhuis-boom-west',
    theme: 'city',
    name: 'The watch house at the boom',
    clue: 'A row of piles closes the harbour. Row to the opening and find the small stone house that keeps watch there.',
    text: 'A row of piles closes the harbour. Ships pass through one opening, the boom. This small watch house of stone stands beside it on a timber platform, with a dark, steep roof.',
  },
  {
    id: 'landmark-handboogdoelen',
    theme: 'city',
    name: 'The hall of the longbow archers',
    clue: 'The archers of the city have a large hall by the city wall, north of the Heiligeweg gate.',
    text: 'The guild of longbow archers finished this hall around 1512. They have their own shooting ground by the city wall. A drawing of 1536, made after Cornelis Anthonisz, shows the hall.',
  },
  {
    id: 'landmark-voetboogdoelen',
    theme: 'city',
    name: 'The hall of the crossbow archers',
    clue: 'Next to the longbow archers a second guild shoots with another bow. Find their simpler hall.',
    text: 'This simpler hall belongs to the guild of crossbow archers. It has a shooting ground of its own, apart from the longbow archers next door. Compare the two halls: which is the larger?',
  },
  {
    id: 'landmark-oude-kerk',
    theme: 'care',
    name: 'The Oude Kerk',
    clue: 'Sailors have a patron saint of their own. Find his great church with its tower, among the harbour houses.',
    text: 'The Oude Kerk was consecrated in 1306 to Sint Nicolaas, the patron saint of sailors. Its tower dates from about 1325. Look up: the pointed spire was added around 1500.',
    source: SOURCES.oldChurch.url,
  },
  {
    id: 'landmark-nieuwe-kerk',
    theme: 'care',
    name: 'The Nieuwe Kerk',
    clue: 'The town needed a second great church. Find it beside the Dam. It has no tower.',
    text: 'More and more people lived in the town, so the bishop approved a second parish church in 1408. The Nieuwe Kerk has no tower. Look for the small turret on the roof instead.',
    source: SOURCES.newChurch.url,
  },
  {
    id: 'landmark-heilige-stede',
    theme: 'care',
    name: 'The Heilige Stede',
    clue: 'Pilgrims come along the Heiligeweg to one chapel. Find the place of the miracle.',
    text: 'People believed that a consecrated wafer survived a fire here in 1345. A chapel was built and pilgrims came along the Heiligeweg. The chapel was rebuilt after the city fire of 1452.',
    source: SOURCES.miracle.url,
  },
  {
    id: 'landmark-begijnhofkapel',
    theme: 'care',
    name: 'The chapel of the Begijnhof',
    clue: 'Religious women who keep their own possessions live around a court with water on every side. Find their chapel.',
    text: 'The beguines are Catholic women who keep their own possessions and help with care and education. Their chapel has a small square bell tower. Only in 1607 does it become the Engelse Kerk.',
    source: SOURCES.beguines.url,
  },
  {
    id: 'landmark-sint-pietersgasthuis',
    theme: 'care',
    name: 'The Sint Pietersgasthuis',
    clue: 'Who cares for you when you have no family nearby? Find the gasthuis on the Nes, built around an open court.',
    text: 'The gasthuis gives meals, shelter and care. It has a bakery and a brewery, and women do much of the daily work. The bread and linen in the court show that work. Where each table stood is our guess.',
    source: SOURCES.gasthuisLife.url,
  },
  {
    id: 'landmark-eerste-weeshuis',
    theme: 'care',
    name: 'The first orphanage',
    clue: 'Orphans have a house in the Kalverstraat, north of the Heilige Stede. Find the large house beside the chapel.',
    text: 'This is the first orphanage of Amsterdam. Its first rules date from 1523. The year it was founded is unknown. The painter of 1538 drew a large house here, beside the chapel.',
  },
  {
    id: 'landmark-leprozenhuis',
    theme: 'care',
    name: 'The Leprozenhuis',
    clue: 'Outside the city, on the Sint Antoniesdijk, stands a walled house with a chapel of its own. Find the house of the lepers.',
    text: 'People with leprosy live here, apart from the town. Everyone feared the illness, although it is not easily passed on. The painting shows a walled compound with a chapel and its bell tower.',
    source: SOURCES.leprozen.url,
  },
  {
    id: 'landmark-kartuizerklooster',
    theme: 'care',
    name: 'The Carthusian monastery',
    clue: 'Far outside the walls, monks live around a great court, each in a cell with a garden of its own.',
    text: 'Carthusian monks live here around a great cloister, each in a separate cell with a garden. Archaeologists found the size of the court in the ground. The painter of 1538 left this monastery out, so much of it is our guess.',
  },
];
